import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const Invoice = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [members, setMembers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  // View mode: 'list' or 'create'
  const [viewMode, setViewMode] = useState('list');
  const [editingInvoice, setEditingInvoice] = useState(null);

  // Invoice Settings (Admin configurable)
  const [invoiceSettings, setInvoiceSettings] = useState({
    gymName: '',
    gymLogo: '',
    gymAddress: '',
    gymPhone: '',
    gymEmail: '',
    gstNumber: '',
    invoicePrefix: 'INV',
    termsAndConditions: 'Thank you for your business!'
  });

  // Invoice Form Data
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    memberId: '',
    memberName: '',
    memberPhone: '',
    memberEmail: '',
    memberAddress: '',
    items: [{ description: 'Gym Membership', quantity: 1, rate: 0, amount: 0 }],
    subtotal: 0,
    taxRate: 18,
    taxAmount: 0,
    discount: 0,
    totalAmount: 0,
    paymentStatus: 'Unpaid',
    notes: ''
  });

  const [showPreview, setShowPreview] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Theme detection
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.body.getAttribute('data-theme') === 'dark' ||
        document.body.classList.contains('dark-mode') ||
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });

    return () => observer.disconnect();
  }, []);

  // Load data
  useEffect(() => {
    fetchData();
    loadInvoiceSettings();
  }, []);

  // Auto-dismiss success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss error message
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchData = async () => {
    try {
      const [membersRes, invoicesRes] = await Promise.all([
        axios.get('/api/members?limit=1000', getAuthHeader()),
        axios.get('/api/invoices?limit=50', getAuthHeader())
      ]);
      setMembers(membersRes.data.members || []);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setErrorMessage('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoiceSettings = async () => {
    try {
      const response = await axios.get('/api/invoices/settings', getAuthHeader());
      setInvoiceSettings(response.data);
    } catch (error) {
      console.error('Failed to load invoice settings:', error);
      // Fall back to localStorage for backwards compatibility
      const saved = localStorage.getItem('invoiceSettings');
      if (saved) {
        setInvoiceSettings(JSON.parse(saved));
      }
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      const response = await axios.get('/api/invoices/next/number', getAuthHeader());
      setInvoiceData(prev => ({
        ...prev,
        invoiceNumber: response.data.invoiceNumber
      }));
    } catch (error) {
      const prefix = invoiceSettings.invoicePrefix || 'INV';
      const date = new Date();
      const year = date.getFullYear().toString().slice(-2);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      setInvoiceData(prev => ({
        ...prev,
        invoiceNumber: `${prefix}-${year}${month}-${random}`
      }));
    }
  };

  const handleMemberSelect = (memberId) => {
    const member = members.find(m => m.id === memberId);
    if (member) {
      setInvoiceData(prev => ({
        ...prev,
        memberId: member.id,
        memberName: member.name,
        memberPhone: member.phone || '',
        memberEmail: member.email || '',
        memberAddress: member.address || ''
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].rate) || 0);
    }
    
    setInvoiceData(prev => ({ ...prev, items: newItems }));
    calculateTotals(newItems);
  };

  const addItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (invoiceData.items.length > 1) {
      const newItems = invoiceData.items.filter((_, i) => i !== index);
      setInvoiceData(prev => ({ ...prev, items: newItems }));
      calculateTotals(newItems);
    }
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = (subtotal * (parseFloat(invoiceData.taxRate) || 0)) / 100;
    const totalAmount = subtotal + taxAmount - (parseFloat(invoiceData.discount) || 0);
    
    setInvoiceData(prev => ({
      ...prev,
      subtotal,
      taxAmount,
      totalAmount
    }));
  };

  const handleTaxOrDiscountChange = (field, value) => {
    setInvoiceData(prev => {
      const newData = { ...prev, [field]: value };
      const subtotal = prev.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
      const taxAmount = (subtotal * (parseFloat(field === 'taxRate' ? value : prev.taxRate) || 0)) / 100;
      const discount = parseFloat(field === 'discount' ? value : prev.discount) || 0;
      const totalAmount = subtotal + taxAmount - discount;
      
      return { ...newData, subtotal, taxAmount, totalAmount };
    });
  };

  const saveInvoice = async () => {
    if (!invoiceData.memberName || !invoiceData.invoiceNumber) {
      setErrorMessage('Customer name and invoice number are required');
      return;
    }
    if (invoiceData.items.every(item => !item.description || item.amount <= 0)) {
      setErrorMessage('Please add at least one valid item');
      return;
    }
    try {
      if (editingInvoice) {
        await axios.put(`/api/invoices/${editingInvoice.id}`, invoiceData, getAuthHeader());
        setSuccessMessage('Invoice updated successfully!');
      } else {
        await axios.post('/api/invoices', invoiceData, getAuthHeader());
        setSuccessMessage('Invoice saved successfully!');
      }
      fetchData();
      resetForm();
      setViewMode('list');
    } catch (error) {
      console.error('Failed to save invoice:', error);
      setErrorMessage(error.response?.data?.message || 'Failed to save invoice');
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`/api/invoices/${id}`, getAuthHeader());
      setSuccessMessage('Invoice deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      setErrorMessage('Failed to delete invoice');
    }
  };

  const editInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceData({
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split('T')[0] : '',
      dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '',
      memberId: invoice.memberId || '',
      memberName: invoice.memberName || '',
      memberPhone: invoice.memberPhone || '',
      memberEmail: invoice.memberEmail || '',
      memberAddress: invoice.memberAddress || '',
      items: invoice.items || [{ description: 'Gym Membership', quantity: 1, rate: 0, amount: 0 }],
      subtotal: invoice.subtotal || 0,
      taxRate: invoice.taxRate || 18,
      taxAmount: invoice.taxAmount || 0,
      discount: invoice.discount || 0,
      totalAmount: invoice.totalAmount || 0,
      paymentStatus: invoice.paymentStatus || 'Unpaid',
      notes: invoice.notes || ''
    });
    setViewMode('create');
  };

  const viewInvoice = (invoice) => {
    setInvoiceData({
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split('T')[0] : '',
      dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '',
      memberId: invoice.memberId || '',
      memberName: invoice.memberName || '',
      memberPhone: invoice.memberPhone || '',
      memberEmail: invoice.memberEmail || '',
      memberAddress: invoice.memberAddress || '',
      items: invoice.items || [],
      subtotal: invoice.subtotal || 0,
      taxRate: invoice.taxRate || 18,
      taxAmount: invoice.taxAmount || 0,
      discount: invoice.discount || 0,
      totalAmount: invoice.totalAmount || 0,
      paymentStatus: invoice.paymentStatus || 'Unpaid',
      notes: invoice.notes || ''
    });
    setShowPreview(true);
  };

  const resetForm = () => {
    setEditingInvoice(null);
    setInvoiceData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      dueDate: '',
      memberId: '',
      memberName: '',
      memberPhone: '',
      memberEmail: '',
      memberAddress: '',
      items: [{ description: 'Gym Membership', quantity: 1, rate: 0, amount: 0 }],
      subtotal: 0,
      taxRate: 18,
      taxAmount: 0,
      discount: 0,
      totalAmount: 0,
      paymentStatus: 'Unpaid',
      notes: ''
    });
  };

  const startNewInvoice = async () => {
    resetForm();
    await generateInvoiceNumber();
    setViewMode('create');
  };

  const updateInvoiceStatus = async (id, status) => {
    try {
      await axios.patch(`/api/invoices/${id}/status`, { paymentStatus: status }, getAuthHeader());
      setSuccessMessage('Status updated!');
      fetchData();
    } catch (error) {
      setErrorMessage('Failed to update status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const printInvoice = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const themeColors = {
    background: isDarkMode ? '#1a1a2e' : '#f5f7fa',
    cardBg: isDarkMode ? '#16213e' : '#ffffff',
    textPrimary: isDarkMode ? '#e8e8e8' : '#1a1a2a',
    textSecondary: isDarkMode ? '#a0a0a0' : '#666666',
    borderColor: isDarkMode ? '#2a2a4a' : '#e0e0e0',
    inputBg: isDarkMode ? '#1a1a2e' : '#ffffff',
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${themeColors.borderColor}`,
    borderRadius: '5px',
    fontSize: '14px',
    background: themeColors.inputBg,
    color: themeColors.textPrimary
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '5px',
    fontWeight: '600',
    color: themeColors.textPrimary,
    fontSize: '13px'
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div style={{ padding: '20px', background: themeColors.background, minHeight: '100vh' }}>
      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#4CAF50',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '5px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#dc3545',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '5px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000
        }}>
          {errorMessage}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <h1 style={{ color: themeColors.textPrimary, margin: 0 }}>🧾 Invoice Generator</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {viewMode === 'list' ? (
            <button
              onClick={startNewInvoice}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              + New Invoice
            </button>
          ) : (
            <button
              onClick={() => { setViewMode('list'); resetForm(); }}
              style={{
                padding: '10px 20px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ← Back to List
            </button>
          )}
        </div>
      </div>

      {/* Invoice List View */}
      {viewMode === 'list' && (
        <div style={{
          background: themeColors.cardBg,
          borderRadius: '10px',
          padding: '20px',
          boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
          border: `1px solid ${themeColors.borderColor}`
        }}>
          <h3 style={{ color: themeColors.textPrimary, marginTop: 0 }}>📋 Invoice History</h3>
          
          {invoices.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: themeColors.textSecondary }}>
              <p style={{ fontSize: '18px' }}>No invoices yet</p>
              <p>Click "New Invoice" to create your first invoice</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: isDarkMode ? '#2a2a4a' : '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Invoice #</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Customer</th>
                    <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Amount</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'center', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr key={invoice.id} style={{ borderBottom: `1px solid ${themeColors.borderColor}` }}>
                      <td style={{ padding: '12px', color: themeColors.textPrimary, fontWeight: '600' }}>{invoice.invoiceNumber}</td>
                      <td style={{ padding: '12px', color: themeColors.textSecondary }}>{formatDate(invoice.invoiceDate)}</td>
                      <td style={{ padding: '12px', color: themeColors.textPrimary }}>{invoice.memberName}</td>
                      <td style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary, fontWeight: '600' }}>₹{parseFloat(invoice.totalAmount).toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <select
                          value={invoice.paymentStatus}
                          onChange={(e) => updateInvoiceStatus(invoice.id, e.target.value)}
                          style={{
                            padding: '5px 10px',
                            borderRadius: '15px',
                            border: 'none',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            cursor: 'pointer',
                            background: invoice.paymentStatus === 'Paid' ? '#28a745' : invoice.paymentStatus === 'Partial' ? '#ffc107' : '#dc3545',
                            color: invoice.paymentStatus === 'Partial' ? '#333' : 'white'
                          }}
                        >
                          <option value="Unpaid">Unpaid</option>
                          <option value="Partial">Partial</option>
                          <option value="Paid">Paid</option>
                        </select>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button onClick={() => viewInvoice(invoice)} style={{ background: '#17a2b8', color: 'white', border: 'none', borderRadius: '3px', padding: '5px 10px', marginRight: '5px', cursor: 'pointer' }} title="View">👁️</button>
                        <button onClick={() => editInvoice(invoice)} style={{ background: '#ffc107', color: '#333', border: 'none', borderRadius: '3px', padding: '5px 10px', marginRight: '5px', cursor: 'pointer' }} title="Edit">✏️</button>
                        <button onClick={() => deleteInvoice(invoice.id)} style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', padding: '5px 10px', cursor: 'pointer' }} title="Delete">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invoice Form */}
      {viewMode === 'create' && (
      <div style={{
        background: themeColors.cardBg,
        borderRadius: '10px',
        padding: '25px',
        boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
        border: `1px solid ${themeColors.borderColor}`
      }}>
        <h3 style={{ color: themeColors.textPrimary, marginTop: 0 }}>
          {editingInvoice ? '✏️ Edit Invoice' : '📝 Create New Invoice'}
        </h3>
        {/* Invoice Details */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px' }}>
          <div>
            <label style={labelStyle}>Invoice Number</label>
            <input
              type="text"
              value={invoiceData.invoiceNumber}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
              style={inputStyle}
              readOnly={!!editingInvoice}
            />
          </div>
          <div>
            <label style={labelStyle}>Invoice Date</label>
            <input
              type="date"
              value={invoiceData.invoiceDate}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, invoiceDate: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Due Date</label>
            <input
              type="date"
              value={invoiceData.dueDate}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, dueDate: e.target.value }))}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Payment Status</label>
            <select
              value={invoiceData.paymentStatus}
              onChange={(e) => setInvoiceData(prev => ({ ...prev, paymentStatus: e.target.value }))}
              style={inputStyle}
            >
              <option value="Unpaid">Unpaid</option>
              <option value="Paid">Paid</option>
              <option value="Partial">Partial</option>
            </select>
          </div>
        </div>

        {/* Member Selection */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: themeColors.textPrimary, marginBottom: '15px' }}>👤 Bill To</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <label style={labelStyle}>Select Member</label>
              <select
                value={invoiceData.memberId}
                onChange={(e) => handleMemberSelect(e.target.value)}
                style={inputStyle}
              >
                <option value="">-- Select Member --</option>
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.name} - {m.phone}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                value={invoiceData.memberName}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, memberName: e.target.value }))}
                style={inputStyle}
                placeholder="Customer name"
              />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input
                type="text"
                value={invoiceData.memberPhone}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, memberPhone: e.target.value }))}
                style={inputStyle}
                placeholder="Phone number"
              />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input
                type="email"
                value={invoiceData.memberEmail}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, memberEmail: e.target.value }))}
                style={inputStyle}
                placeholder="Email address"
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Address</label>
              <input
                type="text"
                value={invoiceData.memberAddress}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, memberAddress: e.target.value }))}
                style={inputStyle}
                placeholder="Customer address"
              />
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div style={{ marginBottom: '25px' }}>
          <h3 style={{ color: themeColors.textPrimary, marginBottom: '15px' }}>📦 Invoice Items</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: isDarkMode ? '#2a2a4a' : '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}` }}>Description</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}`, width: '100px' }}>Qty</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}`, width: '120px' }}>Rate (₹)</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: themeColors.textPrimary, borderBottom: `1px solid ${themeColors.borderColor}`, width: '120px' }}>Amount (₹)</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: `1px solid ${themeColors.borderColor}`, width: '60px' }}></th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.items.map((item, index) => (
                  <tr key={index}>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${themeColors.borderColor}` }}>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        style={{ ...inputStyle, padding: '8px' }}
                        placeholder="Item description"
                      />
                    </td>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${themeColors.borderColor}` }}>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        style={{ ...inputStyle, padding: '8px', textAlign: 'center' }}
                        min="1"
                      />
                    </td>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${themeColors.borderColor}` }}>
                      <input
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                        style={{ ...inputStyle, padding: '8px', textAlign: 'right' }}
                        min="0"
                      />
                    </td>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${themeColors.borderColor}`, textAlign: 'right', color: themeColors.textPrimary, fontWeight: '600' }}>
                      ₹{(item.amount || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '8px', borderBottom: `1px solid ${themeColors.borderColor}`, textAlign: 'center' }}>
                      <button
                        onClick={() => removeItem(index)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '5px 10px',
                          cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            onClick={addItem}
            style={{
              marginTop: '10px',
              padding: '8px 15px',
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            + Add Item
          </button>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: '300px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${themeColors.borderColor}` }}>
              <span style={{ color: themeColors.textSecondary }}>Subtotal:</span>
              <span style={{ color: themeColors.textPrimary, fontWeight: '600' }}>₹{invoiceData.subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${themeColors.borderColor}` }}>
              <span style={{ color: themeColors.textSecondary }}>Tax (GST %):</span>
              <input
                type="number"
                value={invoiceData.taxRate}
                onChange={(e) => handleTaxOrDiscountChange('taxRate', e.target.value)}
                style={{ width: '60px', padding: '5px', textAlign: 'right', border: `1px solid ${themeColors.borderColor}`, borderRadius: '3px', background: themeColors.inputBg, color: themeColors.textPrimary }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${themeColors.borderColor}` }}>
              <span style={{ color: themeColors.textSecondary }}>Tax Amount:</span>
              <span style={{ color: themeColors.textPrimary }}>₹{invoiceData.taxAmount.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${themeColors.borderColor}` }}>
              <span style={{ color: themeColors.textSecondary }}>Discount (₹):</span>
              <input
                type="number"
                value={invoiceData.discount}
                onChange={(e) => handleTaxOrDiscountChange('discount', e.target.value)}
                style={{ width: '80px', padding: '5px', textAlign: 'right', border: `1px solid ${themeColors.borderColor}`, borderRadius: '3px', background: themeColors.inputBg, color: themeColors.textPrimary }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 0', background: 'linear-gradient(135deg, #667eea, #764ba2)', margin: '10px -15px -15px', padding: '15px', borderRadius: '0 0 10px 10px' }}>
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>Total:</span>
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>₹{invoiceData.totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginTop: '25px' }}>
          <label style={labelStyle}>Notes</label>
          <textarea
            value={invoiceData.notes}
            onChange={(e) => setInvoiceData(prev => ({ ...prev, notes: e.target.value }))}
            style={{ ...inputStyle, height: '80px', resize: 'vertical' }}
            placeholder="Additional notes or payment instructions..."
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '25px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowPreview(true)}
            style={{
              padding: '12px 25px',
              background: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            👁️ Preview
          </button>
          <button
            onClick={saveInvoice}
            style={{
              padding: '12px 25px',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            💾 {editingInvoice ? 'Update Invoice' : 'Save Invoice'}
          </button>
        </div>
      </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => setShowPreview(false)}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            {/* Print Actions */}
            <div style={{ padding: '15px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Invoice Preview</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={printInvoice} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: '600' }}>
                  📥 Download PDF
                </button>
                <button onClick={() => setShowPreview(false)} style={{ padding: '10px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>

            {/* Printable Invoice */}
            <div ref={printRef} style={{ padding: '40px', background: 'white', color: '#333' }}>
              <style>{`
                @media print {
                  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                }
              `}</style>
              
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: '2px solid #667eea', paddingBottom: '20px' }}>
                <div>
                  {invoiceSettings.gymLogo && (
                    <img src={invoiceSettings.gymLogo} alt="Logo" style={{ width: '80px', marginBottom: '10px' }} />
                  )}
                  <h1 style={{ margin: '0 0 5px', color: '#667eea', fontSize: '24px' }}>{invoiceSettings.gymName || 'Your Gym Name'}</h1>
                  <p style={{ margin: 0, fontSize: '12px', color: '#666', whiteSpace: 'pre-line' }}>{invoiceSettings.gymAddress}</p>
                  {invoiceSettings.gymPhone && <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#666' }}>📞 {invoiceSettings.gymPhone}</p>}
                  {invoiceSettings.gymEmail && <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#666' }}>✉️ {invoiceSettings.gymEmail}</p>}
                  {invoiceSettings.gstNumber && <p style={{ margin: '5px 0 0', fontSize: '12px', fontWeight: 'bold' }}>GSTIN: {invoiceSettings.gstNumber}</p>}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <h2 style={{ margin: '0 0 10px', color: '#667eea' }}>INVOICE</h2>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Invoice #:</strong> {invoiceData.invoiceNumber}</p>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Date:</strong> {invoiceData.invoiceDate}</p>
                  {invoiceData.dueDate && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Due Date:</strong> {invoiceData.dueDate}</p>}
                  <p style={{ margin: '10px 0 0', padding: '5px 15px', background: invoiceData.paymentStatus === 'Paid' ? '#28a745' : invoiceData.paymentStatus === 'Partial' ? '#ffc107' : '#dc3545', color: 'white', borderRadius: '15px', display: 'inline-block', fontSize: '12px', fontWeight: 'bold' }}>
                    {invoiceData.paymentStatus}
                  </p>
                </div>
              </div>

              {/* Bill To */}
              <div style={{ marginBottom: '30px', padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
                <h4 style={{ margin: '0 0 10px', color: '#667eea' }}>Bill To:</h4>
                <p style={{ margin: '2px 0', fontWeight: 'bold' }}>{invoiceData.memberName}</p>
                {invoiceData.memberAddress && <p style={{ margin: '2px 0', fontSize: '13px' }}>{invoiceData.memberAddress}</p>}
                {invoiceData.memberPhone && <p style={{ margin: '2px 0', fontSize: '13px' }}>📞 {invoiceData.memberPhone}</p>}
                {invoiceData.memberEmail && <p style={{ margin: '2px 0', fontSize: '13px' }}>✉️ {invoiceData.memberEmail}</p>}
              </div>

              {/* Items Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                <thead>
                  <tr style={{ background: '#667eea', color: 'white' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'center', width: '80px' }}>Qty</th>
                    <th style={{ padding: '12px', textAlign: 'right', width: '100px' }}>Rate</th>
                    <th style={{ padding: '12px', textAlign: 'right', width: '100px' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData.items.map((item, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>{item.description}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>₹{parseFloat(item.rate).toFixed(2)}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>₹{parseFloat(item.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: '250px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>Subtotal:</span>
                    <span>₹{invoiceData.subtotal.toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span>GST ({invoiceData.taxRate}%):</span>
                    <span>₹{invoiceData.taxAmount.toFixed(2)}</span>
                  </div>
                  {invoiceData.discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee', color: '#28a745' }}>
                      <span>Discount:</span>
                      <span>-₹{invoiceData.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', background: '#667eea', color: 'white', margin: '10px -10px 0', padding: '12px 10px', fontWeight: 'bold', fontSize: '16px' }}>
                    <span>Total:</span>
                    <span>₹{invoiceData.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Notes & Terms */}
              {(invoiceData.notes || invoiceSettings.termsAndConditions) && (
                <div style={{ marginTop: '30px', padding: '15px', background: '#f8f9fa', borderRadius: '5px', fontSize: '12px' }}>
                  {invoiceData.notes && (
                    <div style={{ marginBottom: '10px' }}>
                      <strong>Notes:</strong>
                      <p style={{ margin: '5px 0 0' }}>{invoiceData.notes}</p>
                    </div>
                  )}
                  {invoiceSettings.termsAndConditions && (
                    <div>
                      <strong>Terms & Conditions:</strong>
                      <p style={{ margin: '5px 0 0' }}>{invoiceSettings.termsAndConditions}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: '40px', textAlign: 'center', color: '#999', fontSize: '11px' }}>
                <p>Thank you for your business!</p>
                <p>Generated by GymKhata - Gym Management System</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoice;
